import { putRequest, getRequest, postRequest, deleteRequest, apiDownload, type ErrorHandler } from "@/services/backendApi";

import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { useAuthStore } from "@/stores/authStore";
import router from "@/router";

/**
 * Public-Token-Modus (Vor-Ort-Fragebogen): Ist store.publicToken gesetzt,
 * werden alle Case-Endpunkte auf /questionnaire-session/... umgeschrieben
 * und OHNE openedAt gesendet (die Token-Endpunkte prüfen keinen Zeitstempel).
 * `/protected/cases/<id-oder-verschlüsselt>/clients/5` → `/questionnaire-session/clients/5?token=…`
 */
function toPublicUrl(endpoint: string, token: string): string {
  const rewritten = endpoint.replace(/^\/protected\/cases\/[^/]+/, "/questionnaire-session");
  const delimiter = rewritten.includes("?") ? "&" : "?";
  return `${rewritten}${delimiter}token=${encodeURIComponent(token)}`;
}

// POST
export function postRequestCase(endpoint: string) {
  const store = useCaseStore();
  if (store.publicToken) {
    return postRequest(toPublicUrl(endpoint, store.publicToken), null, handlePublicApiError, true);
  }
  const openedAt = store.caseData?.openedAt;
  if (!openedAt) throw new Error("Fehlender Zeitstempel. Bitte kontaktieren Sie den Support.");
  return postRequest(appendOpenedAt(endpoint, openedAt), null, handleApiErrorCase, true);
}

// GET
export function getRequestCase(endpoint: string): Promise<any> {
  const store = useCaseStore();
  if (store.publicToken) {
    return getRequest(toPublicUrl(endpoint, store.publicToken), handlePublicApiError, true);
  }
  return getRequest(endpoint, handleApiErrorCase, true);
}

// PUT
export function putRequestCase(endpoint: string, payload: any) {
  const store = useCaseStore();
  if (store.publicToken) {
    return putRequest(toPublicUrl(endpoint, store.publicToken), payload, handlePublicApiError, true);
  }
  const openedAt = store.caseData?.openedAt;
  if (!openedAt) throw new Error("Fehlender Zeitstempel. Bitte kontaktieren Sie den Support.");
  return putRequest(appendOpenedAt(endpoint, openedAt), payload, handleApiErrorCase, true);
}

// DELETE
export function deleteRequestCase(endpoint: string) {
  const store = useCaseStore();
  if (store.publicToken) {
    return deleteRequest(toPublicUrl(endpoint, store.publicToken), null, handlePublicApiError, true);
  }
  const openedAt = store.caseData?.openedAt;
  if (!openedAt) throw new Error("Fehlender Zeitstempel. Bitte kontaktieren Sie den Support.");
  return deleteRequest(appendOpenedAt(endpoint, openedAt), null, handleApiErrorCase, true);
}

function appendOpenedAt(endpoint: string, openedAt: string): string {
  const delimiter = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${delimiter}openedAt=${encodeURIComponent(openedAt)}`;
}

// downloads

export async function apiDownloadSummary(idCase: string | number): Promise<void> {
  const filename = generateTimestampedFilename("Zusammenfassung");
  const endpoint = `/protected/cases/${idCase}/downloads/summary`;
  await apiDownload(endpoint, filename, handleApiErrorCase);
}

export async function apiDownloadPowerOfAttorney(
  idCase: string | number,
  idClient: string | number,
  clientFirstName: string,
  clientLastName: string
): Promise<void> {
  const sanitizedFirstName = clientFirstName.replace(/\s+/g, "_");
  const sanitizedLastName = clientLastName.replace(/\s+/g, "_");
  const baseName = `Vollmacht_${sanitizedFirstName}_${sanitizedLastName}`;
  const filename = generateTimestampedFilename(baseName);
  const endpoint = `/protected/cases/${idCase}/clients/${idClient}/downloads/power-of-attorney`;
  await apiDownload(endpoint, filename, handleApiErrorCase);
}

export async function apiDownloadWitnessForm(idCase: string | number): Promise<void> {
  const filename = generateTimestampedFilename("Zeugenfragebogen");
  const endpoint = `/protected/cases/${idCase}/downloads/witness-form`;
  await apiDownload(endpoint, filename, handleApiErrorCase);
}

/**
 * Unterschriebene digitale Vollmacht (PDF aus der Akte) herunterladen –
 * ersetzt die Papier-Vollmacht aus der Word-Vorlage (Feedback 27.09.2026, VIII).
 */
export async function apiDownloadSignedVollmacht(idCase: string | number, idDocument: number): Promise<void> {
  const filename = generateTimestampedFilename("Vollmacht_unterschrieben");
  const endpoint = `/protected/cases/${idCase}/documents/${idDocument}/download`;
  await apiDownload(endpoint, filename, handleApiErrorCase);
}

/**
 * Fragebogen einreichen (Feedback 27.09.2026, XI). Bewusst ohne
 * handleApiErrorCase: dessen Weiterleitung auf die Fallliste liess den
 * Mandanten mit einer leeren roten Meldung zurueck. Die Fehlermeldung des
 * Servers wird im Dialog angezeigt, ein erneuter Versuch bleibt moeglich.
 */
export async function apiSubmitQuestionnaire(idCase: string | number, openedAt?: string | null): Promise<any> {
  const endpoint = `/protected/cases/submit/${idCase}`;
  const url = openedAt ? appendOpenedAt(endpoint, openedAt) : endpoint;
  return putRequest(url, null, handleSubmitError, true);
}

async function handleSubmitError(response: Response): Promise<never> {
  const data = await response.json().catch(() => ({}));
  const statusCode = data.error_code || response.status;
  const serverMessage = typeof data.error === "string" ? data.error : typeof data.message === "string" ? data.message : "";
  let message: string;
  switch (statusCode) {
    case 401:
      useAuthStore().logout();
      message = "Ihre Sitzung ist abgelaufen, bitte melden Sie sich erneut an.";
      break;
    case 403:
      message = "Sie haben keine Berechtigung, diesen Fragebogen einzureichen.";
      break;
    case 409:
      message = "Der Fall wurde zwischenzeitlich in einer anderen Sitzung bearbeitet. Bitte laden Sie die Seite neu.";
      break;
    default:
      message = serverMessage
        ? `Der Fragebogen konnte nicht gespeichert werden: ${serverMessage}`
        : "Der Fragebogen konnte nicht gespeichert werden. Bitte versuchen Sie es erneut oder wenden Sie sich an Ihre Kanzlei.";
  }
  throw { message, statusCode };
}

export function generateTimestampedFilename(documentName: string): string {
  const now = new Date();

  const pad = (num: number): string => String(num).padStart(2, "0");

  const year = now.getFullYear();
  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const seconds = pad(now.getSeconds());

  return `${documentName}_${year}-${month}-${day}_${hours}-${minutes}-${seconds}.pdf`;
}

// error handling

export async function handleApiErrorCase(response: Response): Promise<never> {
  const data = await response.json().catch(() => ({}));
  const statusCode = data.error_code || response.status;

  let message: string;

  switch (statusCode) {
    case 400:
    case 405:
    case 500:
      router.push({ name: "PageClientsCases" });
      message = "Es ist ein interner Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.";
      break;

    case 401:
      useAuthStore().logout();
      message = "Ihre Sitzung ist abgelaufen, bitte melden Sie sich erneut an.";
      break;

    case 403:
      message = "Anfrage fehlgeschlagen: Sie haben keine Berechtigung, diese Aktion auszuführen.";
      break;

    case 404:
      router.push({ name: "PageClientsCases" });
      message = "Anfrage fehlgeschlagen: Die angeforderte Ressource existiert nicht.";
      break;

    case 409:
      setTimeout(() => {
        router.go(0);
      }, 3000);
      message = "Der Fall wurde zwischenzeitlich in einer anderen Sitzung bearbeitet. Die Seite wird neu geladen.";
      break;

    case 429:
      useAuthStore().logout();
      message = "Anfragelimit erreicht. Bitte versuchen Sie es später erneut.";
      break;

    default:
      message = "Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.";
      break;
  }

  throw { message, statusCode };
}

/** Fehlerbehandlung für die öffentliche Vor-Ort-Seite: keine App-Redirects. */
export async function handlePublicApiError(response: Response): Promise<never> {
  const data = await response.json().catch(() => ({}));
  const statusCode = data.error_code || response.status;
  const store = useCaseStore();
  if ([404, 409, 410].includes(statusCode)) {
    store.publicError = statusCode; // Page.vue rendert Abgelaufen-/Beendet-Screen
  }
  throw {
    statusCode,
    message:
      statusCode === 410
        ? "Dieser Link ist abgelaufen."
        : statusCode === 409
          ? "Diese Sitzung wurde beendet."
          : "Speichern derzeit nicht möglich. Bitte prüfen Sie Ihre Verbindung.",
  };
}
